import { supabase } from '../lib/supabase';

export interface AddressMarker {
  id?: string;
  business_id: string;
  customer_id?: string;
  latitude: number;
  longitude: number;
  marker_type: 'not-home' | 'booked' | 'comeback-later' | 'interested' | 'estimate-sent' | 'bad-customer' | 'customer';
  address?: string;
  notes?: string;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
  customer?: {
    id: string;
    first_name: string;
    last_name: string;
    business_name?: string;
    email?: string;
    phone?: string;
  };
}

export interface CustomerSyncResult {
  created: number;
  updated: number;
  removed: number;
  errors: string[];
}

class AddressMarkerService {
  private getEdgeFunctionUrl(): string {
    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    if (!supabaseUrl) {
      throw new Error('Supabase URL not available');
    }
    return `${supabaseUrl}/functions/v1/address-markers`;
  }

  private async makeRequest<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${this.getEdgeFunctionUrl()}${endpoint}`;
    
    const { data: { session } } = await supabase!.auth.getSession();
    const token = session?.access_token;

    const response = await fetch(url, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
        ...options.headers,
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Network error' }));
      throw new Error(errorData.error || `HTTP ${response.status}`);
    }

    const result = await response.json();
    if (!result.success) {
      throw new Error(result.error || 'Request failed');
    }

    return result.data || result;
  }

  // Sync customers with addresses to create map markers
  async syncCustomers(businessId: string): Promise<CustomerSyncResult> {
    console.log(`Starting customer sync for business: ${businessId}`);
    
    try {
      const result = await this.makeRequest<{ results: CustomerSyncResult }>(`/sync-customers/${businessId}`, {
        method: 'POST',
      });
      
      console.log('Customer sync completed:', result.results);
      return result.results;
    } catch (error) {
      console.error('Error syncing customers:', error);
      throw error;
    }
  }

  // Get all address markers for a business
  async getMarkers(businessId: string): Promise<AddressMarker[]> {
    return this.makeRequest<AddressMarker[]>(`/${businessId}`);
  }

  // Create a new address marker
  async createMarker(marker: Omit<AddressMarker, 'id' | 'created_at' | 'updated_at'>): Promise<AddressMarker> {
    return this.makeRequest<AddressMarker>('', {
      method: 'POST',
      body: JSON.stringify(marker),
    });
  }

  // Update an existing address marker
  async updateMarker(
    markerId: string, 
    updates: Partial<Pick<AddressMarker, 'marker_type' | 'address' | 'notes'>>
  ): Promise<AddressMarker> {
    return this.makeRequest<AddressMarker>(`/${markerId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });
  }

  // Delete an address marker
  async deleteMarker(markerId: string): Promise<void> {
    await this.makeRequest(`/${markerId}`, {
      method: 'DELETE',
    });
  }

  // Helper function to get marker color based on type
  getMarkerColor(markerType: AddressMarker['marker_type']): string {
    const colorMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': '#9CA3AF',        // Gray
      'booked': '#10B981',          // Green
      'comeback-later': '#F59E0B',  // Yellow/Orange
      'interested': '#3B82F6',      // Blue
      'estimate-sent': '#8B5CF6',   // Purple
      'bad-customer': '#EF4444',    // Red
      'customer': '#06B6D4',        // Cyan
    };
    return colorMap[markerType] || '#9CA3AF';
  }

  // Helper function to get marker icon based on type
  getMarkerIcon(markerType: AddressMarker['marker_type']): string {
    const iconMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': '🏠',
      'booked': '✅',
      'comeback-later': '⏰',
      'interested': '👀',
      'estimate-sent': '📄',
      'bad-customer': '❌',
      'customer': '👤',
    };
    return iconMap[markerType] || '📍';
  }

  // Helper function to get human-readable marker type
  getMarkerTypeLabel(markerType: AddressMarker['marker_type']): string {
    const labelMap: Record<AddressMarker['marker_type'], string> = {
      'not-home': 'Not Home',
      'booked': 'Booked',
      'comeback-later': 'Come Back Later',
      'interested': 'Interested',
      'estimate-sent': 'Estimate Sent',
      'bad-customer': 'Bad Customer',
      'customer': 'Customer',
    };
    return labelMap[markerType] || markerType;
  }
}

export const addressMarkerService = new AddressMarkerService(); 